import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import parse from "html-react-parser";
import { getSeoPageBySlug, seoImageUrl } from "../../app/seopages/seopages.services";
import { truncate } from "../../Utils/html";
import Seo from "../../Seo/Seo";
import { webPageSchema } from "../../Seo/jsonld";

/**
 * Admin-managed SEO landing page at /seo-pages/:slug.
 *
 * This route previously had no SPA component at all: food-services served
 * crawlers a standalone EJS document (which didn't even load the React bundle)
 * and let real users fall through to the SPA, where they hit the 404 page.
 * That was both broken for users and a cloaking risk, since the markup
 * crawlers saw was not the markup the site served.
 *
 * Rendering it here means one codepath, and the prerenderer snapshots exactly
 * what users get. Markup reuses the site's existing classes, so pages created
 * in the admin panel inherit the normal design with no extra CSS.
 */
export default function SeoPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);
    setState("loading");

    getSeoPageBySlug(slug)
      .then((data) => {
        if (cancelled) return;
        setPage(data);
        setState(data ? "ready" : "missing");
      })
      .catch(() => {
        if (!cancelled) setState("missing");
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const path = `/seo-pages/${slug}`;

  // An unknown slug must not be indexed, and must not claim to be a real page.
  if (state === "missing") {
    return (
      <main>
        <Seo
          title="Siden blev ikke fundet | Onlineskolemad"
          description="Siden findes ikke."
          path={path}
          index={false}
        />
        <section className="sec-pd rel-z-1">
          <div className="container">
            <div className="title-1 title-center">
              <h1>Siden blev ikke fundet</h1>
              <p>Vi kunne ikke finde den side, du leder efter.</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const title = page?.title || "";
  const categoryName = page?.seoCategory?.categoryName;
  const categorySlug = page?.seoCategory?.categorySlug;
  const bannerUrl = seoImageUrl(page?.imageBanner);


  const description = truncate(page?.description1 || "");

  const sections = [
    [page?.description1, seoImageUrl(page?.img1)],
    [page?.description2, seoImageUrl(page?.img2)],
    [page?.description3, seoImageUrl(page?.img3)],
  ];

  return (
    <main>
      <Seo
        title={title ? `${title} | Onlineskolemad` : undefined}
        description={description}
        path={path}
        image={bannerUrl}
        type="article"
        // While the fetch is in flight there is no content to index yet; the
        // prerenderer waits for `state === "ready"` before snapshotting.
        index={state === "ready"}
        jsonLd={
          state === "ready"
            ? [
                webPageSchema({
                  title,
                  description,
                  url: path,
                  image: bannerUrl,
                }),
              ]
            : undefined
        }
      />

      <section className="page-info-sec">
        <div
          className="bg-fixed wow fadeIn"
          data-wow-delay="0.1s"
          style={{ background: `url("${bannerUrl || "/assets/img/page-info1.png"}")` }}
        />
        <div className="bg-fixed img-overlay wow fadeIn" data-wow-delay="0.2s" />
        <div className="container">
          <div className="row align-items-center justify-content-center">
            <div className="col-md-12">
              <div className="page-info-block wow fadeInUp" data-wow-delay="0.3s">
                <h1>{title}</h1>
                <div className="btm-line-2 text-center justify-content-center">
                  <span />
                  <span />
                </div>
                {description && <p>{description}</p>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {sections.map(([html, imageUrl], index) =>
        html || imageUrl ? (
          <section className="sec-pd rel-z-1" key={index}>
            <div className="container">
              <div className="row align-items-center">
                {imageUrl && (
                  <div className="col-md-6 mmb-20">
                    <div className="img-style-1">
                      <img
                        src={imageUrl}
                        alt={`${title} – billede ${index + 1}`}
                        loading={index === 0 ? undefined : "lazy"}
                      />
                    </div>
                  </div>
                )}
                <div className={imageUrl ? "col-md-6" : "col-md-12"}>
                  <div className="title-1">{html ? parse(html) : null}</div>
                </div>
              </div>
            </div>
          </section>
        ) : null
      )}
    </main>
  );
}
